import React from 'react';
import { Link } from 'react-router-dom';

const HowItWorks: React.FC = () => {
  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container">
          <div className="accent-bar accent-bar-center mb-lg"></div>
          <h1>How It Works</h1>
          <p>
            A straightforward process designed to get you from scattered data to 
            clear direction — without the complexity.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="section process-section">
        {/* Animated Background */}
        <div className="process-bg">
          {/* Vertical data streams */}
          <div className="data-streams">
            <div className="data-stream" style={{ '--x': '8%', '--delay': '0s', '--duration': '8s' } as React.CSSProperties}></div>
            <div className="data-stream" style={{ '--x': '15%', '--delay': '2s', '--duration': '10s' } as React.CSSProperties}></div>
            <div className="data-stream" style={{ '--x': '25%', '--delay': '4s', '--duration': '7s' } as React.CSSProperties}></div>
            <div className="data-stream" style={{ '--x': '78%', '--delay': '1s', '--duration': '9s' } as React.CSSProperties}></div>
            <div className="data-stream" style={{ '--x': '85%', '--delay': '3s', '--duration': '11s' } as React.CSSProperties}></div>
            <div className="data-stream" style={{ '--x': '92%', '--delay': '5s', '--duration': '8s' } as React.CSSProperties}></div>
          </div>
          
          {/* Floating data nodes */}
          <div className="data-nodes">
            <div className="data-node" style={{ '--x': '5%', '--y': '20%', '--delay': '0s' } as React.CSSProperties}></div>
            <div className="data-node" style={{ '--x': '12%', '--y': '60%', '--delay': '1.5s' } as React.CSSProperties}></div>
            <div className="data-node" style={{ '--x': '20%', '--y': '80%', '--delay': '3s' } as React.CSSProperties}></div>
            <div className="data-node" style={{ '--x': '82%', '--y': '25%', '--delay': '0.5s' } as React.CSSProperties}></div>
            <div className="data-node" style={{ '--x': '90%', '--y': '55%', '--delay': '2s' } as React.CSSProperties}></div>
            <div className="data-node" style={{ '--x': '95%', '--y': '85%', '--delay': '4s' } as React.CSSProperties}></div>
          </div>
          
          {/* Horizontal pulse lines */}
          <svg className="pulse-lines" preserveAspectRatio="none">
            <line x1="0" y1="20%" x2="100%" y2="20%" className="pulse-line pl-1"/>
            <line x1="0" y1="50%" x2="100%" y2="50%" className="pulse-line pl-2"/>
            <line x1="0" y1="80%" x2="100%" y2="80%" className="pulse-line pl-3"/>
          </svg>
          
          {/* Connection particles */}
          <div className="connection-particles">
            <div className="conn-particle" style={{ '--startX': '10%', '--startY': '30%', '--endX': '25%', '--endY': '45%', '--delay': '0s' } as React.CSSProperties}></div>
            <div className="conn-particle" style={{ '--startX': '85%', '--startY': '25%', '--endX': '75%', '--endY': '40%', '--delay': '2s' } as React.CSSProperties}></div>
            <div className="conn-particle" style={{ '--startX': '15%', '--startY': '70%', '--endX': '30%', '--endY': '60%', '--delay': '4s' } as React.CSSProperties}></div>
            <div className="conn-particle" style={{ '--startX': '90%', '--startY': '75%', '--endX': '78%', '--endY': '65%', '--delay': '1s' } as React.CSSProperties}></div>
          </div>
          
          {/* Subtle grid */}
          <div className="process-grid"></div>
        </div>
        
        <div className="container process-container">
          <div className="process-header">
            <h2>Our process</h2>
            <p>
              We've refined our approach to minimize disruption while maximizing impact. 
              Here's what working with us looks like.
            </p>
          </div>

          <div className="process-timeline">
            <div className="process-step">
              <div className="process-number">1</div>
              <div className="process-content">
                <h3>Discovery Call (30 min)</h3>
                <p>
                  We start with a conversation, not a sales pitch. We want to understand 
                  your business, your challenges, and what success looks like for you.
                </p>
                <ul>
                  <li>What tools and systems are you using?</li>
                  <li>Where does your data live today?</li>
                  <li>What decisions are hardest to make with current data?</li>
                  <li>What's working? What's not?</li>
                </ul>
              </div>
            </div>

            <div className="process-step">
              <div className="process-number">2</div>
              <div className="process-content">
                <h3>Assessment & Roadmap</h3>
                <p>
                  We dig into your current data landscape and create a clear picture of 
                  where you are and where you can go.
                </p>
                <ul>
                  <li>Map your existing data sources and systems</li>
                  <li>Identify quick wins and long-term opportunities</li>
                  <li>Prioritize based on business impact</li>
                  <li>Deliver a clear, jargon-free roadmap</li>
                </ul>
              </div>
            </div>

            <div className="process-step">
              <div className="process-number">3</div>
              <div className="process-content">
                <h3>Implementation</h3>
                <p>
                  We build and deploy your data infrastructure, keeping you informed 
                  and involved without overwhelming your team.
                </p>
                <ul>
                  <li>Connect your key data sources</li>
                  <li>Build centralized, clean data infrastructure</li>
                  <li>Create dashboards and reports</li>
                  <li>Set up automations as needed</li>
                  <li>Document everything clearly</li>
                </ul>
              </div>
            </div>

            <div className="process-step">
              <div className="process-number">4</div>
              <div className="process-content">
                <h3>Training & Handoff</h3>
                <p>
                  We make sure your team knows how to use what we've built. 
                  No black boxes.
                </p>
                <ul>
                  <li>Walk your team through new dashboards and tools</li>
                  <li>Provide clear documentation</li>
                  <li>Answer questions and address concerns</li>
                  <li>Ensure everyone is comfortable</li>
                </ul>
              </div>
            </div>

            <div className="process-step">
              <div className="process-number">5</div>
              <div className="process-content">
                <h3>Ongoing Support (Optional)</h3>
                <p>
                  For companies who want continued help, we offer flexible ongoing 
                  support that scales with your needs.
                </p>
                <ul>
                  <li>Maintenance and updates</li>
                  <li>New metrics and dashboards as you grow</li>
                  <li>On-call analytics help</li>
                  <li>Strategic advice on new initiatives</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section principles-section-v2">
        {/* Animated Background */}
        <div className="principles-bg">
          <svg className="principles-waves" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path className="wave wave-1" d="M0,160 C320,220 420,120 640,160 C860,200 960,100 1200,140 C1320,160 1380,180 1440,160 L1440,320 L0,320 Z"/>
            <path className="wave wave-2" d="M0,200 C200,160 400,240 640,200 C880,160 1080,240 1440,200 L1440,320 L0,320 Z"/>
          </svg>
          <div className="principles-particles">
            <div className="p-particle" style={{ '--x': '10%', '--y': '30%', '--delay': '0s' } as React.CSSProperties}></div>
            <div className="p-particle" style={{ '--x': '25%', '--y': '70%', '--delay': '1s' } as React.CSSProperties}></div>
            <div className="p-particle" style={{ '--x': '45%', '--y': '25%', '--delay': '2s' } as React.CSSProperties}></div>
            <div className="p-particle" style={{ '--x': '60%', '--y': '80%', '--delay': '0.5s' } as React.CSSProperties}></div>
            <div className="p-particle" style={{ '--x': '75%', '--y': '40%', '--delay': '1.5s' } as React.CSSProperties}></div>
            <div className="p-particle" style={{ '--x': '90%', '--y': '65%', '--delay': '2.5s' } as React.CSSProperties}></div>
          </div>
        </div>

        <div className="container principles-container">
          <div className="principles-header-v2">
            <div className="accent-bar accent-bar-center mb-lg"></div>
            <h2>Our principles</h2>
            <p>How we think about working with clients.</p>
          </div>

          <div className="principles-grid-v2">
            {/* Outcomes First */}
            <div className="principle-card-v2" style={{ '--delay': '0s' } as React.CSSProperties}>
              <div className="principle-icon-v2">
                <svg viewBox="0 0 60 60">
                  {/* Target rings */}
                  <circle className="target-ring ring-outer" cx="30" cy="30" r="26" fill="none" stroke="var(--color-seafoam)" strokeWidth="2"/>
                  <circle className="target-ring ring-middle" cx="30" cy="30" r="18" fill="none" stroke="var(--color-turquoise)" strokeWidth="2" opacity="0.7"/>
                  <circle className="target-ring ring-inner" cx="30" cy="30" r="10" fill="none" stroke="var(--color-turquoise)" strokeWidth="2"/>
                  {/* Bullseye */}
                  <circle className="target-center" cx="30" cy="30" r="4" fill="var(--color-green)"/>
                  {/* Arrow */}
                  <g className="target-arrow">
                    <line x1="30" y1="30" x2="50" y2="10" stroke="var(--color-navy)" strokeWidth="2" strokeLinecap="round"/>
                    <polygon points="52,6 46,12 50,16" fill="var(--color-navy)"/>
                  </g>
                </svg>
              </div>
              <h3>Outcomes First</h3>
              <p>We focus on business results, not technical complexity. Every solution is measured by the impact it creates.</p>
            </div>

            {/* No Lock-In */}
            <div className="principle-card-v2" style={{ '--delay': '0.1s' } as React.CSSProperties}>
              <div className="principle-icon-v2">
                <svg viewBox="0 0 60 60">
                  {/* Open lock body */}
                  <rect className="lock-body" x="18" y="28" width="24" height="20" rx="3" fill="var(--color-turquoise)"/>
                  {/* Open shackle */}
                  <path className="lock-shackle" d="M24,28 L24,20 C24,13 30,8 36,8 C42,8 42,13 42,20" fill="none" stroke="var(--color-navy)" strokeWidth="4" strokeLinecap="round"/>
                  {/* Key */}
                  <g className="lock-key">
                    <circle cx="30" cy="38" r="3" fill="var(--color-pearl)"/>
                    <rect x="28" y="40" width="4" height="6" fill="var(--color-pearl)"/>
                  </g>
                  {/* Sparkles */}
                  <circle className="lock-sparkle s1" cx="48" cy="14" r="2" fill="var(--color-green)"/>
                  <circle className="lock-sparkle s2" cx="52" cy="22" r="1.5" fill="var(--color-yellow)"/>
                </svg>
              </div>
              <h3>No Lock-In</h3>
              <p>Everything we build belongs to you. We document thoroughly and use standard tools so you're never dependent on us.</p>
            </div>

            {/* Plain Language */}
            <div className="principle-card-v2" style={{ '--delay': '0.2s' } as React.CSSProperties}>
              <div className="principle-icon-v2">
                <svg viewBox="0 0 60 60">
                  {/* Speech bubble */}
                  <path className="speech-bubble" d="M10,12 L50,12 C52,12 54,14 54,16 L54,36 C54,38 52,40 50,40 L24,40 L16,50 L16,40 L10,40 C8,40 6,38 6,36 L6,16 C6,14 8,12 10,12 Z" fill="var(--color-seafoam)" stroke="var(--color-turquoise)" strokeWidth="2"/>
                  {/* Text lines */}
                  <line className="speech-line line-1" x1="14" y1="22" x2="46" y2="22" stroke="var(--color-navy)" strokeWidth="2" strokeLinecap="round"/>
                  <line className="speech-line line-2" x1="14" y1="30" x2="38" y2="30" stroke="var(--color-navy)" strokeWidth="2" strokeLinecap="round"/>
                  {/* Check mark */}
                  <path className="speech-check" d="M42,28 L45,32 L52,24" fill="none" stroke="var(--color-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Plain Language</h3>
              <p>No jargon, no unnecessary complexity. We explain everything in terms that make sense to your business.</p>
            </div>

            {/* Start Small, Grow Smart */}
            <div className="principle-card-v2" style={{ '--delay': '0.3s' } as React.CSSProperties}>
              <div className="principle-icon-v2">
                <svg viewBox="0 0 60 60">
                  {/* Growing bars */}
                  <rect className="grow-bar bar-1" x="8" y="42" width="10" height="12" rx="2" fill="var(--color-seafoam)"/>
                  <rect className="grow-bar bar-2" x="22" y="32" width="10" height="22" rx="2" fill="var(--color-turquoise)" opacity="0.7"/>
                  <rect className="grow-bar bar-3" x="36" y="22" width="10" height="32" rx="2" fill="var(--color-turquoise)"/>
                  {/* Growth arrow */}
                  <path className="grow-arrow" d="M10,38 Q25,25 48,12" fill="none" stroke="var(--color-green)" strokeWidth="2" strokeLinecap="round"/>
                  <polygon className="grow-arrow-head" points="52,8 44,12 48,16" fill="var(--color-green)"/>
                  {/* Seed/sprout */}
                  <circle className="grow-seed" cx="13" cy="50" r="2" fill="var(--color-navy)"/>
                </svg>
              </div>
              <h3>Start Small, Grow Smart</h3>
              <p>We believe in proving value quickly. Start with what matters most, then expand as you see results.</p>
            </div>

            {/* Partnership */}
            <div className="principle-card-v2" style={{ '--delay': '0.4s' } as React.CSSProperties}>
              <div className="principle-icon-v2">
                <svg viewBox="0 0 60 60">
                  {/* Two hands meeting */}
                  <path className="hand hand-left" d="M8,32 L18,32 L22,28 L26,32 L26,40 L18,40 L8,40 Z" fill="var(--color-turquoise)"/>
                  <path className="hand hand-right" d="M52,32 L42,32 L38,28 L34,32 L34,40 L42,40 L52,40 Z" fill="var(--color-navy)"/>
                  {/* Handshake overlap */}
                  <ellipse className="handshake-center" cx="30" cy="36" rx="8" ry="6" fill="var(--color-seafoam)"/>
                  {/* Connection lines */}
                  <path className="connect-arc" d="M20,22 Q30,14 40,22" fill="none" stroke="var(--color-green)" strokeWidth="2" strokeLinecap="round"/>
                  {/* Heart/trust symbol */}
                  <path className="trust-heart" d="M30,18 L28,16 C26,14 26,11 28,10 C30,9 30,9 30,11 C30,9 30,9 32,10 C34,11 34,14 32,16 Z" fill="var(--color-green)"/>
                </svg>
              </div>
              <h3>Partnership, Not Just Service</h3>
              <p>We care about your success. That means honest advice, even when it's not what you want to hear.</p>
            </div>

            {/* Practical Over Perfect */}
            <div className="principle-card-v2" style={{ '--delay': '0.5s' } as React.CSSProperties}>
              <div className="principle-icon-v2">
                <svg viewBox="0 0 60 60">
                  {/* Lightning bolt */}
                  <polygon className="lightning-bolt" points="34,8 22,28 30,28 26,52 40,30 32,30" fill="var(--color-yellow)" stroke="var(--color-navy)" strokeWidth="1.5"/>
                  {/* Speed lines */}
                  <line className="speed-line sl-1" x1="12" y1="20" x2="18" y2="20" stroke="var(--color-turquoise)" strokeWidth="2" strokeLinecap="round"/>
                  <line className="speed-line sl-2" x1="10" y1="30" x2="18" y2="30" stroke="var(--color-turquoise)" strokeWidth="2" strokeLinecap="round"/>
                  <line className="speed-line sl-3" x1="14" y1="40" x2="20" y2="40" stroke="var(--color-turquoise)" strokeWidth="2" strokeLinecap="round"/>
                  {/* Checkmark badge */}
                  <circle className="practical-badge" cx="48" cy="44" r="8" fill="var(--color-green)"/>
                  <path className="practical-check" d="M44,44 L47,47 L52,40" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Practical Over Perfect</h3>
              <p>Good enough today beats perfect never. We build solutions that work now and can evolve later.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-inner">
            <h2>Ready to get started?</h2>
            <p>
              Book a free 30-minute discovery call. No sales pitch — 
              just a conversation about your business and how we might help.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-green btn-lg">
                Schedule a Call
              </Link>
              <Link to="/services" className="btn btn-outline btn-lg">
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HowItWorks;
